import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Loader2, FileText, ListChecks, ClipboardList, MessageSquare, BarChart3, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { SessionStatusBadge } from "@/components/jarvis/SessionStatusBadge";
import { SessionTimer } from "@/components/jarvis/SessionTimer";
import { MetricsPanel } from "@/components/jarvis/MetricsPanel";
import { JarvisTranscriptStream } from "@/components/jarvis/JarvisTranscriptStream";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

type Session = {
  id: string;
  status: string;
  mode: string;
  event_name: string | null;
  event_description: string | null;
  started_at: string | null;
  ended_at: string | null;
  total_turns: number;
  total_cost_usd: number;
  total_llm_tokens: number;
  total_stt_ms: number;
};

type Artifact = {
  id: string;
  artifact_type: string;
  title: string | null;
  content: string;
  content_structured: any;
  delivery_status: string;
  created_at: string;
};

const ARTIFACT_TYPES: Record<string, { label: string; icon: any }> = {
  summary: { label: "Resumo", icon: FileText },
  action_items: { label: "Action Items", icon: ListChecks },
  meeting_minutes: { label: "Ata", icon: ClipboardList },
  transcript_export: { label: "Transcrição", icon: MessageSquare },
};

export default function JarvisSessionReview() {
  const { id } = useParams<{ id: string }>();
  const [session, setSession] = useState<Session | null>(null);
  const [artifacts, setArtifacts] = useState<Record<string, Artifact>>({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState<string | null>(null);

  const loadAll = useCallback(async () => {
    if (!id) return;
    const [{ data: s }, { data: arts }] = await Promise.all([
      supabase.from("jarvis_sessions").select("id, status, mode, event_name, event_description, started_at, ended_at, total_turns, total_cost_usd, total_llm_tokens, total_stt_ms").eq("id", id).single(),
      supabase.from("jarvis_artifacts").select("id, artifact_type, title, content, content_structured, delivery_status, created_at").eq("session_id", id).order("created_at", { ascending: false }),
    ]);
    if (!s) { toast.error("Sessão não encontrada"); setLoading(false); return; }
    setSession(s as Session);
    const map: Record<string, Artifact> = {};
    for (const a of (arts ?? []) as Artifact[]) {
      if (!map[a.artifact_type]) map[a.artifact_type] = a; // pega o mais recente
    }
    setArtifacts(map);
    setLoading(false);
  }, [id]);

  useEffect(() => { void loadAll(); }, [loadAll]);

  // Realtime: novos artefatos aparecem automaticamente
  useEffect(() => {
    if (!id) return;
    const ch = supabase
      .channel(`jarvis-artifacts-${id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "jarvis_artifacts", filter: `session_id=eq.${id}` }, () => { void loadAll(); })
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [id, loadAll]);

  const generate = async (type: string) => {
    if (!id) return;
    setGenerating(type);
    try {
      const { data, error } = await supabase.functions.invoke("jarvis-artifacts", {
        body: { action: "generate", session_id: id, artifact_types: [type] },
      });
      if (error) throw error;
      if (data?.success === false) throw new Error(data.error ?? "generation_failed");
      toast.success("Artefato gerado");
      await loadAll();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (/no_transcripts_found/i.test(msg)) {
        toast.warning("Esta sessão não tem nenhuma fala registrada. Inicie uma nova sessão e fale com o Jarvis antes de gerar artefatos.");
      } else if (/not.found|404|FunctionNotFound|FetchError/i.test(msg)) {
        toast.info("Geração de artefatos será habilitada no próximo sprint.");
      } else {
        toast.error(`Falha: ${msg}`);
      }
    } finally {
      setGenerating(null);
    }
  };

  if (loading || !session) {
    return (
      <div className="container mx-auto px-4 py-12 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const renderArtifactTab = (type: string) => {
    const meta = ARTIFACT_TYPES[type];
    const art = artifacts[type];
    if (!art) {
      return (
        <Card>
          <CardContent className="py-12 flex flex-col items-center text-center">
            <meta.icon className="w-10 h-10 text-muted-foreground mb-3" />
            <h3 className="text-base font-semibold mb-1">Sem {meta.label.toLowerCase()} ainda</h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-md">
              Gere automaticamente a partir da transcrição completa desta sessão.
            </p>
            <Button onClick={() => generate(type)} disabled={generating === type}>
              {generating === type ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Gerar {meta.label}
            </Button>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <meta.icon className="w-4 h-4" /> {art.title || meta.label}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px]">
              {format(new Date(art.created_at), "dd/MM HH:mm", { locale: ptBR })}
            </Badge>
            <Button size="sm" variant="ghost" onClick={() => generate(type)} disabled={generating === type}>
              {generating === type ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />} Regenerar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {type === "action_items" && Array.isArray(art.content_structured?.items) ? (
            <ul className="space-y-2">
              {art.content_structured.items.map((it: any, i: number) => (
                <li key={i} className="rounded-md border border-border/50 p-3 text-sm">
                  <div className="font-medium">{it.task ?? it.title ?? `Item ${i + 1}`}</div>
                  {it.owner && <div className="text-xs text-muted-foreground mt-0.5">Responsável: {it.owner}</div>}
                  {it.due && <div className="text-xs text-muted-foreground">Prazo: {it.due}</div>}
                </li>
              ))}
            </ul>
          ) : (
            <pre className="whitespace-pre-wrap text-sm font-sans leading-relaxed">{art.content}</pre>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-6xl">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="sm">
            <Link to="/jarvis/history"><ArrowLeft className="w-4 h-4 mr-1" /> Histórico</Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight font-headline">
              {session.event_name || "Sessão Jarvis"}
            </h1>
            {session.event_description && (
              <p className="text-sm text-muted-foreground mt-0.5 max-w-2xl">{session.event_description}</p>
            )}
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <SessionStatusBadge status={session.status} />
              <Badge variant="outline" className="capitalize">{session.mode}</Badge>
              <SessionTimer startedAt={session.started_at} endedAt={session.ended_at} />
              {!["completed", "abandoned", "error"].includes(session.status) && (
                <Button asChild size="sm" variant="outline">
                  <Link to={`/jarvis/session/${session.id}`}>Voltar para a sessão ao vivo</Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="summary" className="w-full">
        <TabsList className="grid w-full max-w-2xl grid-cols-5">
          <TabsTrigger value="summary"><FileText className="w-4 h-4 mr-1" /> Resumo</TabsTrigger>
          <TabsTrigger value="action_items"><ListChecks className="w-4 h-4 mr-1" /> Ações</TabsTrigger>
          <TabsTrigger value="meeting_minutes"><ClipboardList className="w-4 h-4 mr-1" /> Ata</TabsTrigger>
          <TabsTrigger value="transcript"><MessageSquare className="w-4 h-4 mr-1" /> Transcrição</TabsTrigger>
          <TabsTrigger value="metrics"><BarChart3 className="w-4 h-4 mr-1" /> Métricas</TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="mt-4">{renderArtifactTab("summary")}</TabsContent>
        <TabsContent value="action_items" className="mt-4">{renderArtifactTab("action_items")}</TabsContent>
        <TabsContent value="meeting_minutes" className="mt-4">{renderArtifactTab("meeting_minutes")}</TabsContent>

        <TabsContent value="transcript" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <MessageSquare className="w-4 h-4" /> Transcrição completa
              </CardTitle>
              {!artifacts.transcript_export && (
                <Button size="sm" variant="ghost" onClick={() => generate("transcript_export")} disabled={generating === "transcript_export"}>
                  {generating === "transcript_export" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />} Exportar markdown
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="h-[500px]">
                <JarvisTranscriptStream sessionId={session.id} maxItems={500} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="metrics" className="mt-4">
          <MetricsPanel
            stats={{
              total_turns: session.total_turns,
              total_cost_usd: session.total_cost_usd,
              total_llm_tokens: session.total_llm_tokens,
              total_stt_ms: session.total_stt_ms,
            }}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
